import { Alert, Card, Col, Progress, Row, Skeleton, Statistic, Table, Tag, Typography } from 'antd';
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { errorMessage } from '../../api/client';
import { useFixedAccount } from '../../api/hooks';
import type { FixedLedgerEntry, PartyType } from '../../types';
import { date, money } from '../../utils/format';

const TYPE_COLOR: Record<string, string> = {
  Investment: 'blue',
  Withdrawal: 'orange',
};

interface MonthPoint {
  label: string;
  given: number;
  received: number;
}

/** Last 12 calendar months of money in and money out, oldest first. */
function monthlyFlow(ledger: FixedLedgerEntry[]): MonthPoint[] {
  const months: MonthPoint[] = [];
  const index = new Map<string, MonthPoint>();
  const now = new Date();

  for (let i = 11; i >= 0; i -= 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${d.getMonth()}`;
    const point: MonthPoint = {
      label: d.toLocaleDateString(undefined, { month: 'short', year: '2-digit' }),
      given: 0,
      received: 0,
    };
    months.push(point);
    index.set(key, point);
  }

  for (const entry of ledger) {
    const d = new Date(entry.date);
    const point = index.get(`${d.getFullYear()}-${d.getMonth()}`);
    if (!point) continue; // older than 12 months
    point.given += entry.invested;
    point.received += entry.withdrawn;
  }

  return months;
}

interface Props {
  partyType: PartyType;
  partyId: number;
  /** "My" wording for portals; staff pages read better in the third person. */
  possessive?: string;
}

/**
 * Overview for a fixed-return account. The percentage dashboard's figures (accrued profit, daily
 * earning, rate) have no meaning here, so this shows the agreed total being drawn down instead.
 */
export default function FixedDashboard({ partyType, partyId, possessive = 'My' }: Props) {
  const { data, isLoading, error } = useFixedAccount(partyType, partyId);

  if (error) return <Alert type="error" showIcon message={errorMessage(error)} />;
  if (isLoading || !data) return <Skeleton active paragraph={{ rows: 6 }} />;

  const receivedShare =
    data.totalReturnAgreed > 0 ? Math.min(100, Math.round((data.totalWithdrawn / data.totalReturnAgreed) * 100)) : 0;
  const flow = monthlyFlow(data.ledger);
  const activeCount = data.investments.filter((i) => i.isActive).length;

  return (
    <>
      {data.hasOverdue && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="One or more investments are past their return date."
        />
      )}

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title={`${possessive} total invested`}
              value={money(data.totalInvested)}
              prefix="PKR"
              valueStyle={{ fontSize: 20, color: '#1677ff' }}
            />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {activeCount} open investment{activeCount === 1 ? '' : 's'} of {data.investments.length}
            </Typography.Text>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Total return agreed"
              value={money(data.totalReturnAgreed)}
              prefix="PKR"
              valueStyle={{ fontSize: 20 }}
            />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Includes PKR {money(data.totalProfit)} profit
            </Typography.Text>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Received so far"
              value={money(data.totalWithdrawn)}
              prefix="PKR"
              valueStyle={{ fontSize: 20, color: '#3f8600' }}
            />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {data.withdrawals.length} withdrawal{data.withdrawals.length === 1 ? '' : 's'}
            </Typography.Text>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Remaining to receive"
              value={money(data.totalRemaining)}
              prefix="PKR"
              valueStyle={{ fontSize: 20, color: data.totalRemaining > 0 ? '#cf1322' : '#3f8600' }}
            />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {data.nextDueDate ? `Next due ${date(data.nextDueDate)}` : 'Nothing outstanding'}
            </Typography.Text>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={10}>
          <Card title={`${possessive} account`} size="small">
            <Row gutter={[8, 12]}>
              <Col span={12}>
                <Statistic title="Total invested" value={money(data.totalInvested)} valueStyle={{ fontSize: 16 }} />
              </Col>
              <Col span={12}>
                <Statistic title="Profit agreed" value={money(data.totalProfit)} valueStyle={{ fontSize: 16 }} />
              </Col>
              <Col span={12}>
                <Statistic title="Total received" value={money(data.totalWithdrawn)} valueStyle={{ fontSize: 16 }} />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Investing since"
                  value={data.investments.length ? date(data.investments[0].investDate) : '—'}
                  valueStyle={{ fontSize: 16 }}
                />
              </Col>
            </Row>

            <div style={{ marginTop: 16 }}>
              <Typography.Text style={{ fontSize: 12 }}>Received out of the agreed return</Typography.Text>
              <Progress percent={receivedShare} strokeColor="#52c41a" format={(p) => `${p}%`} />
            </div>
          </Card>
        </Col>

        <Col xs={24} lg={14}>
          <Card title="Money in vs money back — last 12 months" size="small">
            <div style={{ width: '100%', height: 240 }}>
              <ResponsiveContainer>
                <BarChart data={flow} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" tickLine={false} fontSize={12} />
                  <YAxis tickFormatter={(v: number) => money(v)} width={80} tickLine={false} fontSize={12} />
                  <Tooltip formatter={(v: number) => `PKR ${money(v)}`} />
                  <Legend />
                  <Bar dataKey="given" fill="#1677ff" radius={[4, 4, 0, 0]} name="Invested" />
                  <Bar dataKey="received" fill="#fa8c16" radius={[4, 4, 0, 0]} name="Withdrawn" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </Col>
      </Row>

      <Card title="Recent transactions" size="small" style={{ marginTop: 16 }}>
        <Table<FixedLedgerEntry>
          dataSource={[...data.ledger].reverse().slice(0, 10)}
          rowKey={(row) => `${row.type}-${row.refId}`}
          pagination={false}
          size="small"
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: 'No transactions yet' }}
          columns={[
            { title: 'Date', dataIndex: 'date', render: date },
            {
              title: 'Type',
              dataIndex: 'type',
              render: (value: string) => <Tag color={TYPE_COLOR[value] ?? 'default'}>{value}</Tag>,
            },
            {
              title: 'Amount',
              align: 'right',
              render: (_, row) => money(row.type === 'Investment' ? row.invested : row.withdrawn),
            },
            { title: 'Remaining after', dataIndex: 'remainingAfter', align: 'right', render: money },
          ]}
        />
      </Card>
    </>
  );
}
